import { type MouseEvent, type ReactNode, type Ref } from 'react';
import { buttonClassName, type ButtonAppearance, type ButtonSize, type ButtonVariant } from './buttonClasses';

interface ButtonProps {
  children?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  size?: ButtonSize;
  // Colour intent. Pair a colour (primary, secondary, neutral, info, warning, danger) with `appearance`;
  // the `outline`, `ghost` and `clear` variants remain only for existing call sites.
  variant?: ButtonVariant;
  appearance?: ButtonAppearance;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  rounded?: boolean;
  // Accessible-name contract: icon-only buttons (rendered with `startIcon`
  // and no `children`) have no text node for screen readers to announce.
  // `ariaLabel` is the canonical fix — `Tooltip` does NOT set an accessible
  // name (it's a visual-only hint), so it must not be relied on as a
  // substitute. Optional + maps directly to the native `aria-label`
  // attribute, preserving backwards compatibility for buttons with text
  // children where an explicit override is not needed.
  ariaLabel?: string;
  // Links the button to persistent helper text (e.g. the reason it is
  // disabled) so assistive tech can announce it. Maps directly to the native
  // `aria-describedby` attribute.
  ariaDescribedBy?: string;
  // Maps to the native `aria-pressed` attribute for a toggle button (e.g. a
  // select-all control) whose current state is binary and visible.
  ariaPressed?: boolean;
  // Disclosure state for a button that shows/hides a region. `ariaControls` must
  // carry the id of that region so assistive tech can reach it.
  ariaExpanded?: boolean;
  ariaControls?: string;
  // Focusable "disabled": announces aria-disabled and no-ops activation, so the
  // reason (via ariaDescribedBy) stays reachable. Native `disabled` is inert.
  ariaDisabled?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  type = 'submit',
  size = 'md',
  variant = 'primary',
  appearance = 'solid',
  startIcon,
  endIcon,
  onClick,
  className = '',
  disabled = false,
  rounded = true,
  ariaLabel,
  ariaDescribedBy,
  ariaPressed,
  ariaExpanded,
  ariaControls,
  ariaDisabled = false,
  ref,
}) => {
  const isInert = disabled || ariaDisabled;
  const inertFocusRing =
    !disabled && ariaDisabled
      ? 'aria-disabled:focus-visible:outline-2 aria-disabled:focus-visible:outline-offset-2 aria-disabled:focus-visible:outline-mp-foreground'
      : '';

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (!disabled && ariaDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.();
  };

  return (
    <button
      ref={ref}
      type={type}
      className={`${buttonClassName({ variant, appearance, size, rounded, className })} ${isInert ? 'cursor-not-allowed opacity-50' : ''} ${inertFocusRing}`}
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-disabled={!disabled && ariaDisabled ? true : undefined}
    >
      {startIcon && <span className="flex items-center">{startIcon}</span>}
      {children}
      {endIcon && <span className="flex items-center">{endIcon}</span>}
    </button>
  );
};
